import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Send, Users } from 'lucide-react';
import {
  type ChatMessage,
  type Conversation,
  createConversation,
  listConversations,
  listMessages,
  markChatRead,
  reactToMessage,
  sendMessage,
} from '@/api/chat';
import { useDirectory } from '@/hooks/useDirectory';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';

const REACTIONS = ['👍', '❤️', '🎉', '😂', '🙏'];

// activeMentionToken finds an in-progress "@token" ending at the caret: an '@'
// at the start or after whitespace, with no whitespace between it and the caret.
// Returns the '@' offset + the typed query (without spaces) for the typeahead.
function activeMentionToken(text: string, caret: number): { start: number; query: string } | null {
  for (let i = caret - 1; i >= 0; i--) {
    const ch = text[i];
    if (ch === '@') {
      if (i === 0 || /\s/.test(text[i - 1])) return { start: i, query: text.slice(i + 1, caret) };
      return null;
    }
    if (/\s/.test(ch)) return null;
  }
  return null;
}

// renderMessageBody highlights resolved @mentions in a message body.
function renderMessageBody(body: string | null, mentionedNames: string[]): React.ReactNode {
  const text = body ?? '';
  if (mentionedNames.length === 0) return text;
  const escaped = mentionedNames.map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp('@(?:' + escaped.join('|') + ')', 'g');
  const parts: React.ReactNode[] = [];
  let last = 0;
  for (let m = re.exec(text); m !== null; m = re.exec(text)) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    parts.push(
      <span
        key={m.index}
        className="rounded bg-[var(--color-lop-teal-glow)] px-0.5 font-medium text-[var(--color-lop-teal)]"
      >
        {m[0]}
      </span>,
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}

export function MessagesPage() {
  const { t } = useTranslation();
  const dir = useDirectory();
  const myId = useAuthStore((s) => s.user?.userId);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [draft, setDraft] = useState('');
  const [showNew, setShowNew] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // @mentions: ids the user picked from the typeahead, and the active query.
  const [mentions, setMentions] = useState<{ id: string; name: string }[]>([]);
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);

  const mentionMatches =
    mentionQuery === null
      ? []
      : [...dir.users.values()]
          .filter((u) => u.id !== myId)
          .map((u) => ({ id: u.id, name: dir.nameFor(u.id) }))
          .filter((u) => u.name.toLowerCase().includes(mentionQuery.toLowerCase()))
          .slice(0, 6);

  function onDraftChange(value: string, caret: number) {
    setDraft(value);
    const tok = activeMentionToken(value, caret);
    setMentionQuery(tok ? tok.query : null);
  }

  function pickMention(user: { id: string; name: string }) {
    const caret = inputRef.current?.selectionStart ?? draft.length;
    const tok = activeMentionToken(draft, caret);
    const start = tok ? tok.start : caret;
    const before = draft.slice(0, start);
    const after = draft.slice(caret);
    const insert = `@${user.name} `;
    setDraft(before + insert + after);
    setMentions((prev) => (prev.some((m) => m.id === user.id) ? prev : [...prev, user]));
    setMentionQuery(null);
    requestAnimationFrame(() => {
      const pos = (before + insert).length;
      inputRef.current?.focus();
      inputRef.current?.setSelectionRange(pos, pos);
    });
  }

  const loadConversations = useCallback(() => {
    listConversations()
      .then((list) => {
        setConversations(list);
        setSelected((cur) => cur ?? list[0]?.id ?? null);
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  const loadMessages = useCallback((chatId: string) => {
    setLoadingMsgs(true);
    listMessages(chatId)
      .then((m) => setMessages(m.slice().reverse())) // backend returns newest-first
      .catch(() => setMessages([]))
      .finally(() => setLoadingMsgs(false));
    markChatRead(chatId).catch(() => undefined);
  }, []);

  useEffect(() => {
    if (selected) loadMessages(selected);
  }, [selected, loadMessages]);

  // Live chat (utilizable, epic 54b57f4): silently poll the open conversation
  // every 5s so new messages appear without a manual reload. No spinner toggle.
  useEffect(() => {
    if (!selected) return;
    const handle = setInterval(() => {
      listMessages(selected)
        .then((m) => setMessages(m.slice().reverse()))
        .catch(() => undefined);
    }, 5000);
    return () => clearInterval(handle);
  }, [selected]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  function convLabel(c: Conversation): string {
    if (c.name) return c.name;
    return t(`chat.type.${c.chatType}`);
  }

  async function submit() {
    if (!draft.trim() || !selected) return;
    const body = draft.trim();
    // Keep only mentions whose @name still appears in the final body (the user
    // may have deleted one after picking it).
    const ids = mentions.filter((m) => body.includes('@' + m.name)).map((m) => m.id);
    setDraft('');
    setMentions([]);
    setMentionQuery(null);
    await sendMessage(selected, body, undefined, ids.length ? ids : undefined);
    loadMessages(selected);
  }

  return (
    <div className="flex h-[calc(100vh-4rem)] flex-col p-6">
      <header className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-[var(--color-lop-text-primary)]">
            {t('chat.title')}
          </h1>
          <p className="text-sm text-[var(--color-lop-text-secondary)]">{t('chat.subtitle')}</p>
        </div>
        <Button size="sm" onClick={() => setShowNew(true)} data-testid="chat-new">
          <Plus className="mr-1 h-4 w-4" />
          {t('chat.new')}
        </Button>
      </header>

      <div className="flex min-h-0 flex-1 gap-4">
        {/* Conversation list */}
        <aside className="w-64 shrink-0 overflow-y-auto rounded-lg border bg-card" data-testid="chat-list">
          {conversations.length === 0 && (
            <p className="p-4 text-sm text-muted-foreground">{t('chat.empty')}</p>
          )}
          <ul className="divide-y divide-[var(--color-lop-border)]">
            {conversations.map((c) => (
              <li key={c.id}>
                <button
                  type="button"
                  onClick={() => setSelected(c.id)}
                  className={`flex w-full items-center gap-2 px-3 py-2.5 text-left text-sm transition-colors hover:bg-[var(--color-lop-bg-card-hi)] ${
                    selected === c.id ? 'bg-[var(--color-lop-bg-card-hi)]' : ''
                  }`}
                >
                  <Users className="h-4 w-4 text-[var(--color-lop-text-muted)]" />
                  <span className="truncate text-[var(--color-lop-text-primary)]">{convLabel(c)}</span>
                </button>
              </li>
            ))}
          </ul>
        </aside>

        {/* Thread */}
        <section className="flex min-w-0 flex-1 flex-col rounded-lg border bg-card">
          {!selected && (
            <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
              {t('chat.selectPrompt')}
            </div>
          )}
          {selected && (
            <>
              <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto p-4" data-testid="chat-thread">
                {loadingMsgs && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}
                {!loadingMsgs && messages.length === 0 && (
                  <p className="text-sm text-muted-foreground">{t('chat.noMessages')}</p>
                )}
                {messages.map((m) => {
                  const mine = m.senderUserId === myId;
                  return (
                    <div key={m.id} className={`flex flex-col ${mine ? 'items-end' : 'items-start'}`}>
                      <span className="mb-0.5 text-[10px] text-[var(--color-lop-text-muted)]">
                        {dir.nameFor(m.senderUserId)}
                      </span>
                      <div
                        className={`group relative max-w-[75%] rounded-2xl px-3 py-2 text-sm ${
                          mine
                            ? 'bg-[var(--color-lop-teal)] text-[var(--color-lop-bg-deep)]'
                            : 'bg-[var(--color-lop-bg-card-hi)] text-[var(--color-lop-text-primary)]'
                        }`}
                      >
                        <span className="whitespace-pre-wrap">
                        {renderMessageBody(
                          m.body,
                          (m.mentionedUserIds ?? []).map((id) => dir.nameFor(id)).filter(Boolean),
                        )}
                      </span>
                        <span className="absolute -top-2 right-0 hidden gap-0.5 rounded-full border border-[var(--color-lop-border)] bg-[var(--color-lop-bg-card)] px-1 group-hover:flex">
                          {REACTIONS.map((e) => (
                            <button
                              key={e}
                              type="button"
                              className="text-xs hover:scale-125"
                              onClick={() => reactToMessage(m.id, e)}
                              aria-label={`react ${e}`}
                            >
                              {e}
                            </button>
                          ))}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="relative flex items-center gap-2 border-t border-[var(--color-lop-border)] p-3">
                {mentionMatches.length > 0 && (
                  <ul
                    className="absolute bottom-full left-3 mb-1 w-64 overflow-hidden rounded-lg border border-[var(--color-lop-border)] bg-[var(--color-lop-bg-card)] shadow-lg"
                    data-testid="chat-mention-list"
                  >
                    {mentionMatches.map((u) => (
                      <li key={u.id}>
                        <button
                          type="button"
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-[var(--color-lop-text-primary)] hover:bg-[var(--color-lop-bg-card-hi)]"
                          onMouseDown={(e) => {
                            e.preventDefault(); // keep input focus
                            pickMention(u);
                          }}
                        >
                          <span className="text-[var(--color-lop-teal)]">@</span>
                          {u.name}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <Input
                  ref={inputRef}
                  value={draft}
                  onChange={(e) => onDraftChange(e.target.value, e.target.selectionStart ?? e.target.value.length)}
                  onKeyDown={(e) => {
                    if (e.key === 'Escape' && mentionQuery !== null) {
                      setMentionQuery(null);
                      return;
                    }
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void submit();
                    }
                  }}
                  placeholder={t('chat.composer.placeholder')}
                  data-testid="chat-composer"
                />
                <Button size="sm" onClick={submit} disabled={!draft.trim()}>
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </>
          )}
        </section>
      </div>

      {showNew && (
        <NewConversationModal
          onClose={() => setShowNew(false)}
          onCreated={(id) => {
            setShowNew(false);
            loadConversations();
            setSelected(id);
          }}
        />
      )}
    </div>
  );
}

function NewConversationModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { t } = useTranslation();
  const dir = useDirectory();
  const myId = useAuthStore((s) => s.user?.userId);
  const [name, setName] = useState('');
  const [members, setMembers] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);

  const others = [...dir.users.values()].filter((u) => u.id !== myId);

  function toggle(id: string) {
    setMembers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function create() {
    setSubmitting(true);
    try {
      const { id } = await createConversation({
        chatType: 'group',
        name: name.trim() || undefined,
        memberIds: [...members],
      });
      onCreated(id);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[92vh] w-full max-w-md overflow-y-auto rounded-lg border bg-card p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold">{t('chat.newModal.title')}</h3>
        <label className="mb-1 block text-xs text-[var(--color-lop-text-secondary)]">
          {t('chat.newModal.name')}
        </label>
        <Input value={name} onChange={(e) => setName(e.target.value)} className="mb-4" />
        <p className="mb-2 text-xs text-[var(--color-lop-text-secondary)]">
          {t('chat.newModal.members')}
        </p>
        <div className="mb-4 max-h-48 space-y-1 overflow-y-auto rounded border border-[var(--color-lop-border)] p-2">
          {others.length === 0 && (
            <p className="text-xs text-muted-foreground">{t('chat.newModal.noUsers')}</p>
          )}
          {others.map((u) => (
            <label key={u.id} className="flex cursor-pointer items-center gap-2 text-sm">
              <input type="checkbox" checked={members.has(u.id)} onChange={() => toggle(u.id)} />
              {dir.nameFor(u.id)}
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button size="sm" onClick={create} disabled={submitting} data-testid="chat-new-submit">
            {t('chat.newModal.create')}
          </Button>
        </div>
      </div>
    </div>
  );
}
